import { FC, useState, useEffect } from "react";
import { InputType } from "../../../types/InputType";
import { FormikProps } from "formik";
import { useTranslation } from "react-i18next";
import { SystemEventsSearchFiltersDTO } from "../../../validation-schemas/SystemEventsSearchFiltersFields";
import BaseModal from "../../modals/BaseModal";
import FilterIconSVG from "../../shared/svg/FilterIconSVG";
import CustomInput from "../../../custom-components/form-inputs/CustomInput";
import { systemEventsSearchFiltersFields } from "../../../validation-schemas/SystemEventsSearchFiltersFields";
import CustomDropdown from "../../../custom-components/form-inputs/CustomDropdown";
import { SystemEventParentType } from "../../../domain/enums/SystemEventParentTypeEnum";
import { SystemEventType } from "../../../domain/enums/SystemEventTypeEnum";
import { enumToSelectProps } from "../../../tools/EnumTools";
import { GET_ALL_RECORDS } from "../../../domain/constants";

interface SystemEventsSearchFiltersFormProps {
    form: FormikProps<SystemEventsSearchFiltersDTO>;
    filtersFields: SystemEventsSearchFiltersDTO;
}

const SystemEventsSearchFiltersForm: FC<SystemEventsSearchFiltersFormProps> = (props) => {
    const { t } = useTranslation();
    const [isVisible, setIsVisible] = useState(false);
    const confirmHandler = () => {
        setIsVisible(false);
        props.form.handleSubmit();
    };
    const closeHandler = () => {
        setIsVisible(false);
    };
    const onOpenHandler = () => {
        props.form.setValues(props.filtersFields);
    };

    useEffect(() => {
        if (isVisible) {
            props.form.setValues(props.filtersFields);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isVisible]);

    const parentTypeOptions = [
        { value: GET_ALL_RECORDS, labelKey: "console.common.all" },
        ...enumToSelectProps(SystemEventParentType, "console.systemEvents.parentTypes"),
    ];

    const eventTypeOptions = [
        { value: GET_ALL_RECORDS, labelKey: "console.common.all" },
        ...enumToSelectProps(SystemEventType, "console.systemEvents.eventTypes"),
    ];

    const parentTypeDefaultValue = props.form.values.parentType !== undefined && props.form.values.parentType !== null
        ? parentTypeOptions.find(opt => opt.value === props.form.values.parentType)
        : parentTypeOptions.find(opt => opt.value === GET_ALL_RECORDS);

    const eventTypeDefaultValue = props.form.values.eventType !== undefined && props.form.values.eventType !== null && props.form.values.eventType !== GET_ALL_RECORDS
        ? eventTypeOptions.find(opt => opt.value === props.form.values.eventType)
        : eventTypeOptions.find(opt => opt.value === GET_ALL_RECORDS);

    return (
        <BaseModal
            isVisible={isVisible}
            setIsVisible={setIsVisible}
            title={t("console.common.filters")}
            confirmTitle={t("console.common.apply")}
            cancelTitle={t("console.common.cancel")}
            buttonElement={
                <button
                    data-modal-target="static-modal"
                    data-modal-toggle="static-modal"
                    className="flex items-center text-white bg-light-bg-300 dark:bg-dark-bg-300 hover:bg-light-bg-300 dark:hover:bg-dark-bg-300 focus:ring-4 focus:outline-none font-medium rounded-lg text-sm px-4 py-2.5 text-center max-h-10"
                    type="button"
                    title={t("console.common.filters")}
                    onClick={() => setIsVisible(true)}
                >
                    <FilterIconSVG className="w-[20px] fill-white" />
                </button>
            }
            confirmHandler={confirmHandler}
            closeHandler={closeHandler}
            onOpen={onOpenHandler}
            additionalWrapClassName="w-[450px]"
        >
            <CustomInput
                type={systemEventsSearchFiltersFields.parentId.type as InputType}
                value={props.form.values.parentId}
                name="parentId"
                placeholderKey={systemEventsSearchFiltersFields.parentId.placeholderKey}
                errorKey={props.form.errors.parentId as string}
                onChange={props.form.handleChange}
            />
            <CustomDropdown
                data={parentTypeOptions}
                defaultValue={parentTypeDefaultValue}
                dropDownCallback={(value) => {
                    try {
                        const numValue = typeof value === 'number' ? value : Number(value);
                        if (!isNaN(numValue) && numValue !== GET_ALL_RECORDS) {
                            props.form.setFieldValue("parentType", numValue);
                        } else {
                            props.form.setFieldValue("parentType", undefined);
                        }
                    } catch (error) {
                        console.error('Error setting parentType:', error);
                        props.form.setFieldValue("parentType", undefined);
                    }
                }}
                placeHolderKey={systemEventsSearchFiltersFields.parentType.placeholderKey}
                errorKey={props.form.errors.parentType as string}
                wrapperClassName="mb-6"
                buttonClassName="w-[190px]"
                dropdownClassName="w-[190px]"
            />
            <CustomDropdown
                data={eventTypeOptions}
                defaultValue={eventTypeDefaultValue}
                dropDownCallback={(value) => {
                    try {
                        const numValue = typeof value === 'number' ? value : Number(value);
                        if (!isNaN(numValue) && numValue !== GET_ALL_RECORDS) {
                            props.form.setFieldValue("eventType", numValue);
                        } else {
                            props.form.setFieldValue("eventType", undefined);
                        }
                    } catch (error) {
                        console.error('Error setting eventType:', error);
                        props.form.setFieldValue("eventType", undefined);
                    }
                }}
                placeHolderKey={systemEventsSearchFiltersFields.eventType.placeholderKey}
                errorKey={props.form.errors.eventType as string}
                wrapperClassName="mb-6"
                buttonClassName="w-[190px]"
                dropdownClassName="w-[190px]"
            />
        </BaseModal>
    );
};

export default SystemEventsSearchFiltersForm;
